'use strict'

import { hasNotEmptyItems, prepareFilesDataToUpload } from '../helpers.js'
import { FileDocument, FileFolder } from './FileDocument.js'
import AbstractFilesGroup from './AbstractFilesGroup.js'

export default class FilesGroup extends AbstractFilesGroup {
  constructor (config) {
    super(config)

    this.key = config.key || config.categoryKey
    this.title = config.title || ''
    this.readonly = !!config.readonly
    this.property = config.property || null // default: 'documents' TODO
    this.categoryKey = config.categoryKey || null
    this.documentType = config.documentType || null
    this.props = config.props
    this.enrichmentFn = config.enrichmentFn || null

    this.files = []
    this.filesThumbnails = []
    this.selectedFiles = []
    this.filesToUpload = []
    this.filesToUpdate = []
    this.filesToDelete = []

    this.loading = false

    let folders = config.folders
    this.filesTree = []
    this.foldersById = {}
    this.createFolderFile({ id: -1, name: 'root' }, {
      children: this.filesTree,
      hierarchy: !!folders // TODO зачем ?
    }, this.foldersById)

    if (folders) {
      this.hierarchy = true
      folders = folders.map(folder => this.createFolderFile(folder, null, this.foldersById))
      folders.forEach(fileWrapper => this.addFileToTree(fileWrapper, fileWrapper.parentId, this.foldersById))
    } else {
      this.list = true
    }

    const documents = config.documents
    if (documents) {
      if (documents instanceof Promise) {
        this.loading = true
        documents
          .then(documents => {
            this.pushFiles(documents)
          })
          .catch(ignored => {})
          .finally(() => {
            this.loading = false
          })
      } else {
        this.pushFiles(documents)
      }
    }
  }

  /**
   * Ключ свойства
   */
  setProperty (property) {
    this.property = property
  }

  pushFiles (documents) {
    documents.forEach(document => this.pushFile(document))
  }

  pushFile (document, config) {
    if (this.enrichmentFn) {
      if (config) {
        const groupConfig = this.enrichmentFn(document)
        if (groupConfig) {
          config = Object.assign(groupConfig, config)
        }
      } else {
        config = this.enrichmentFn(document)
      }
    }

    if (this.hierarchy) {
      const foldersIds = document.foldersIds || [-1]
      const fileWrappers = []
      for (var folderIndex = 0; folderIndex < foldersIds.length; folderIndex++) {
        const fileWrapper = new FileDocument(document, config, folderIndex)
        this.files.push(fileWrapper)
        this.filesThumbnails.push(fileWrapper.smallUrl)

        this.addFileToTree(fileWrapper, foldersIds[folderIndex], this.foldersById)
        fileWrappers.push(fileWrapper)
      }
      return fileWrappers
    } else {
      const fileWrapper = new FileDocument(document, config)
      this.files.push(fileWrapper)
      this.filesThumbnails.push(fileWrapper.smallUrl)

      if (this.list) {
        this.addFileToTree(fileWrapper, -1) // TODO remove
      }
      return fileWrapper
    }
  }

  get size () {
    return (this.files.length || 0) + (this.filesToUpload.length || 0)
  }

  get empty () {
    return this.files.length === 0 && this.filesToUpload.length === 0
  }

  // private
  addFileToTree (fileWrapper, folderId, foldersById = this.foldersById) {
    const parentFileWrapper = this.getFolder(folderId, foldersById)
    this.addFileToFolder(fileWrapper, parentFileWrapper)
  }

  deleteSelectedFiles () {
    this.selectedFiles.slice().forEach(fileWrapper => this.deleteFile(fileWrapper))
    // console.log('U: ' + this.filesToUpload.map(file => file.name || file.name).join(', '))
    // console.log('D: ' + this.filesToDelete.map(file => file.name || file.name).join(', '))
  }

  deleteAllFiles () {
    this.files.slice().forEach(fileWrapper => {
      this.deleteFile(fileWrapper)
    })
  }

  deleteFile (fileWrapper) {
    FilesGroup.deleteElementFromArray(this.files, fileWrapper)
    FilesGroup.deleteElementFromArray(this.filesThumbnails, fileWrapper.smallUrl)
    FilesGroup.deleteElementFromArray(this.selectedFiles, fileWrapper)
    FilesGroup.deleteElementFromArray(this.filesToUpload, fileWrapper)
    FilesGroup.deleteElementFromArray(this.filesToUpdate, fileWrapper)

    if (!fileWrapper.isToUpload) {
      fileWrapper.isToDelete = true
      this.filesToDelete.push(fileWrapper)
    }

    if (this.hierarchy || this.list) { // fileWrapper.parent
      if (fileWrapper._node.children) {
        fileWrapper._node.children.slice().forEach(childNode => this.deleteFile(childNode._file))
      }
      const parentChildren = fileWrapper.parent._node.children
      FilesGroup.deleteElementFromArray(parentChildren, fileWrapper._node, false)
    }
  }

  selectFile (file, selectedFiles = this.selectedFiles) { // TODO rename selectItem
    if (!selectedFiles.includes(file)) {
      selectedFiles.push(file)
      file.selected = true
    } else {
      const index = selectedFiles.indexOf(file)
      if (index !== -1) {
        selectedFiles.splice(index, 1)
        file.selected = false
      }
    }
  }

  getSelectedFiles () {
    return this.selectedFiles
  }

  getSelectedFilesData () {
    return this.getSelectedFiles().map(fileWrapper => {
      return {
        file: fileWrapper,
        filesGroup: this
      }
    })
  }

  clearSelection () {
    if (this.selectedFiles.length) {
      this.selectedFiles.splice(0, this.selectedFiles.length)
        .forEach(file => {
          file.selected = false
        })
    }
  }

  reset () {
    this.filesToUpload = []
    this.filesToUpdate = []

    this.filesToDelete.forEach(fileWrapper => {
      this.files.push(fileWrapper)
      this.filesThumbnails.push(fileWrapper.smallUrl)
    })
    this.filesToDelete = []
  }

  clear () {
    this.files = []
    this.filesThumbnails = []
    this.selectedFiles = []
    this.filesToUpload = []
    this.filesToUpdate = []
    this.filesToDelete = []
  }

  hasFiles () {
    return this.hasFilesToUpload() || this.files.find(fileWrapper => !fileWrapper.isToDelete) != null
  }

  getFilesToUpdate () {
    return this.filesToUpdate.filter(fileWrapper => !fileWrapper.isToUpload && !fileWrapper.isToDelete)
  }

  // hasChanges () {
  //   return super.hasChanges()
  // }

  hasFilesToUpload () {
    return hasNotEmptyItems(this.filesToUpload)
  }

  hasFilesToUpdate () {
    return hasNotEmptyItems(this.filesToUpdate)
  }

  hasFilesToDelete () {
    return hasNotEmptyItems(this.filesToDelete)
  }

  static deleteElementFromArray (array, element) {
    if (array && array.length && element != null) {
      // TODO typeof element !== 'string' => проверять по el.url ?
      const index = array.findIndex(el => el === element || el.id != null && el.id === element.id)
      if (index !== -1) {
        array.splice(index, 1)
      }
    }
  }

  /**
   * Работа с папками
   */
  addFolder (name) {
    const parentFileWrapper = this.getSelectedFolder(true)
    if (parentFileWrapper) {
      let newId = 1
      Object.keys(this.foldersById).forEach(id => {
        if (newId <= +id) {
          newId = +id + 1
        }
      })

      const fileWrapper = this.createFolderFile({
        id: newId,
        parentId: parentFileWrapper.id,
        name: name
      }, {
        isToUpload: true
      })
      this.addFileToFolder(fileWrapper, parentFileWrapper, true)
      return fileWrapper

      // TODO перенести
      // this.filesToUpload.push(fileWrapper)
    }
  }

  createFolderFile (folder, folderConfig, foldersById = this.foldersById) {
    const fileWrapper = new FileFolder(folder, folderConfig)
    foldersById[folder.id] = fileWrapper
    return fileWrapper
  }

  getRoot () {
    return this.getFolder()
  }

  getFolder (id = -1, foldersById = this.foldersById) {
    let fileWrapper = foldersById[id]
    if (!fileWrapper) {
      // TODO check folderPath
      fileWrapper = foldersById[-1]
    }
    return fileWrapper
  }

  getSelectedFolder (defaultRoot) {
    if (!this.hierarchy) {
      return this.list ? this.getFolder(-1) : null
    }
    if (this.selectedFiles.length <= 1) {
      let parentId = null
      if (this.selectedFiles.length === 1) {
        const fileWrapper = this.selectedFiles[0]
        if (fileWrapper.type === 'folder') {
          parentId = fileWrapper.id
        }
      } else {
        parentId = -1
      }
      if (parentId != null) {
        return this.getFolder(parentId)
      }
    }
    // Выбран документ или выбрано несколько папок/документов
    return defaultRoot != null ? this.getFolder(-1) : null
  }

  addFile (fileWrapper) {
    const parentFileWrapper = this.getSelectedFolder(true)
    this.addFileToFolder(fileWrapper, parentFileWrapper)
    return fileWrapper
  }

  addFileToFolder (fileWrapper, parentFileWrapper) {
    if (fileWrapper.isToUpload && !this.filesToUpload.includes(fileWrapper)) {
      this.filesToUpload.push(fileWrapper)
    }
    if (this.hierarchy || this.list) {
      parentFileWrapper._node.children.push(fileWrapper._node)
      parentFileWrapper._node.children.sort((a, b) => {
        const fileA = a._file
        const fileB = b._file
        if (fileA.createTime && fileB.createTime) {
          return fileB.createTime - fileA.createTime // новые файлы выше
        } else if (fileA.createTime) {
          return -1 // файлы выше папок
        } else if (fileB.createTime) {
          return 1
        }
        return 0
      })
      fileWrapper.parent = parentFileWrapper
    }
  }

  moveFilesToFolder (filesWrappers, parentFileWrapper) {
    filesWrappers.forEach(fileWrapper => {
      this.moveFileToFolder(fileWrapper, parentFileWrapper)
    })
  }

  moveFileToFolder (fileWrapper, parentFileWrapper) {
    if (!parentFileWrapper) {
      parentFileWrapper = this.getRoot()
    }
    if (fileWrapper.type === 'file' || (fileWrapper.type === 'folder' && this.checkMoveAbility(fileWrapper, parentFileWrapper))) {
      if (fileWrapper.parent) {
        fileWrapper.oldParent = fileWrapper.parent
        FilesGroup.deleteElementFromArray(fileWrapper.parent._node.children, fileWrapper._node)
        fileWrapper.parent = null
      }
      if (!this.filesToUpdate.includes(fileWrapper)) {
        this.filesToUpdate.push(fileWrapper)
      }
      this.addFileToFolder(fileWrapper, parentFileWrapper)
      return true
    }
    return false
  }

  checkMoveAbility (fileWrapper, parentFileWrapper) {
    let parentId = parentFileWrapper._node.nodeKey
    while (parentId != -1) {
      if (parentId == fileWrapper._node.nodeKey) {
        return false
      }
      parentId = this.getFolder(parentId).parent._node.nodeKey
    }
    return true
  }

  // === Upload

  getFilesToUploadData () {
    const filesGroup = this
    if (filesGroup.filesToUpload.length === 0) {
      return []
    }

    if (!this.sourceConfig) {
      throw Error('Группа файлов не настроена для загрузки')
    }
    const objectId = this.sourceConfig.objectId
    const preloadFormId = this.sourceConfig.preloadFormId
    const url = this.sourceConfig.uploadURL || '/service/delta/document/upload'

    return prepareFilesDataToUpload({
      objectId,
      preloadFormId,
      url,
      files: filesGroup.filesToUpload,
      categoryKey: filesGroup.categoryKey,
      documentType: filesGroup.documentType,
      property: filesGroup.property
    })
  }

  // === Update

  getFilesToUpdateData () {
    if (!this.hierarchy) {
      return []
    }

    const filesRels = []
    this.getFilesToUpdate().forEach(fileWrapper => filesRels.push(this.getFileToUpdateData(fileWrapper)))
    this.filesToUpload.forEach((fileWrapper) => {
      if (fileWrapper.type === 'folder') {
        filesRels.push(this.getFileToUpdateData(fileWrapper))
      }
    })
    return filesRels
  }

  // @private
  getFileToUpdateData (fileWrapper) {
    let fileRel
    if (fileWrapper.type === 'folder') {
      fileRel = {
        id: fileWrapper.id,
        folder: true,
        name: fileWrapper.name
      }
      if (fileWrapper.isToUpload) {
        fileRel.new = true
        // fileRel.name = fileWrapper.name
        // fileRel.path = getFilePath(fileWrapper)
      }
    } else {
      fileRel = {
        id: fileWrapper.document.relId,
        folder: false,
        name: fileWrapper.name
      }
    }

    fileRel.parentId = fileWrapper.parent.id

    if (fileWrapper.oldParent) {
      fileRel.oldParentId = fileWrapper.oldParent.id
    }

    Object.defineProperty(fileRel, 'file', {
      value: fileWrapper,
      writable: false,
      enumerable: false,
      configurable: false
    })
    Object.defineProperty(fileRel, 'filesGroup', {
      value: this,
      writable: false,
      enumerable: false,
      configurable: false
    })

    return fileRel
  }

  // === Remove

  getFilesToRemoveData () {
    if (!this.sourceConfig || !this.sourceConfig.removeDelayed) {
      return []
    }
    return this.filesToDelete.map(fileWrapper => {
      return {
        file: fileWrapper,
        filesGroup: this
      }
    })
  }

  removeFile (fileWrapper, target) {
    if (fileWrapper.document && fileWrapper.document._preloaded && this.sourceConfig && this.sourceConfig.preloadFormId) {
      return this.removePreloadedFile(fileWrapper, this, target)
    } else if (!this.sourceConfig || !this.sourceConfig.removeDelayed) {
      return this.removeFiles({
        removeFilesData: [{ file: fileWrapper, filesGroup: this }],
        progress: false,
        target: target
      })
    }
    this.deleteFile(fileWrapper)
  }

  // ===

  containsFile (fileWrapper) {
    if (fileWrapper) {
      if (fileWrapper.id != null) {
        return this.files.find(fw => fw.id === fileWrapper.id) != null
      } else {
        return this.files.includes(fileWrapper)
      }
    }
    return false
  }

  findFileById (id) {
    if (id != null) {
      return this.files.find(fw => fw.id === id)
    }
    return null
  }

  findFileIndexById (id) {
    if (id != null) {
      return this.files.findIndex(fw => fw.id === id)
    }
    return null
  }
}
